/**
 * PHARMAKON — the team workstation in the browser (X XWP-72; consumer of XWP-70 / XWP-71 as built; CP
 * §15.3 « Shared equipe@officine_name.com session », §11, §15.21-1; XO-12, XD-222, XD-293…XD-296, XD-322,
 * XD-327…XD-330).
 *
 * One owner of what the browser keeps and decides for a shared workstation:
 *
 *  · the workstation — opened by the owner's team login + code (`POST /auth/equipe/verification`): its team
 *    token and refresh token live in localStorage under their own key (`CLES_SESSION_EQUIPE.POSTE`), never
 *    under the user's `pharmakon_token`; the team token is renewed through `POST /auth/equipe/renouveler`,
 *    ONCE across the windows of the browser (Web Locks + one promise per window); a 409
 *    SESSION_POSTE_DEJA_RENOUVELEE means « another window rotated it »: the stored pair is reread and the
 *    rotated token is NEVER sent again (a later replay revokes the workstation, XD-294);
 *  · the user picked by PIN — their own JWT under `pharmakon_token` (authStore), no refresh token, and a
 *    selection marker (`CLES_SESSION_EQUIPE.SELECTION`: id, deadline, last prolongation). While they are
 *    active the JWT is renewed through `POST /auth/equipe/selection/prolonger` (never `/auth/refresh`), at
 *    most once per step; idle, the screen goes back to the picker just before the server's deadline;
 *  · « Changer d'utilisateur » and the idle return forget the user's browser state — their token, the
 *    caisse windows' tab mirror and snapshots — and reload the picker (every in-memory store goes with the
 *    page), while the workstation and the user's caisse login session id stay: the server drafts persist
 *    (their holds are released by the server's inactivity TTL, XD-24);
 *  · a window that sees another window switch user follows it (storage event): no window keeps acting on
 *    a screen of the previous user with the next user's token;
 *  · the owner's step-up (XD-296): a 403 PREUVE_SESSION_EQUIPE_REQUISE opens the password prompt once;
 *    the API client replays the request after `POST /auth/equipe/preuve`.
 *
 * The pure parts (mirror options, filters, the PIN pad reducer, the refusal sentences, the prolongation
 * rule) are unit-tested in `lib/__tests__/session-equipe-xwp72.test.js`.
 */
import { create } from "zustand";
import { brancherSessionEquipe } from "@/services/api.client.js";
import { equipeApi } from "@/services/session-equipe.api.js";
import { useCaisseFenetresStore } from "@/stores/caisseFenetresStore.js";
import { cleMiroir } from "@/lib/caisse-fenetres.js";
import { LOCALE_LABELS, fmtDate, fmtTime } from "@/config/locale.config.js";
import {
  CLES_SESSION_EQUIPE as K,
  CODES_SESSION_EQUIPE as CODES,
  ETAPES_CONNEXION,
  MESSAGES_SESSION_EQUIPE as M,
  MOTIFS_RETOUR_CHOIX,
  RAISONS_NON_SELECTIONNABLE,
  ROUTES_SESSION_EQUIPE as R,
  SESSION_EQUIPE_WEB as W,
} from "@/config/session-equipe.config.js";

// ── storage ─────────────────────────────────────────────────────────────────

const local = () => (typeof localStorage !== "undefined" ? localStorage : null);
const onglet = () => (typeof sessionStorage !== "undefined" ? sessionStorage : null);

function lireJson(store, cle) {
  try {
    const v = store?.getItem(cle);
    return v ? JSON.parse(v) : null;
  } catch {
    return null;
  }
}
function ecrireJson(store, cle, v) {
  try {
    if (v == null) store?.removeItem(cle);
    else store?.setItem(cle, JSON.stringify(v));
  } catch {
    /* private mode, quota — the server stays the authority */
  }
}

/** The workstation (team token, refresh token, session, account, pharmacy) or null. */
export const lirePoste = () => lireJson(local(), K.POSTE);

/**
 * Keep the answer of `verification` or `renouveler` as the workstation; what the renewal does not repeat
 * (account, pharmacy, limitation) is kept from before.
 */
export function enregistrerPoste(r) {
  const prec = lirePoste() ?? {};
  const poste = {
    access_token: r.access_token,
    expires_at: r.expires_at ?? null,
    refresh_token: r.refresh_token,
    session: r.session ?? prec.session ?? null,
    compte: r.compte ?? prec.compte ?? null,
    pharmacie: r.pharmacie ?? prec.pharmacie ?? null,
    limitation: r.limitation ?? prec.limitation ?? null,
  };
  ecrireJson(local(), K.POSTE, poste);
  return poste;
}

/** Forget the workstation (closed, revoked, expired) and its picked user. */
export function effacerPoste() {
  ecrireJson(local(), K.POSTE, null);
  ecrireJson(local(), K.SELECTION, null);
}

/** True while a workstation session is stored and not past its saved duration. */
export function posteOuvert(maintenant = Date.now()) {
  const p = lirePoste();
  if (!p?.refresh_token) return false;
  const fin = p.session?.expires_at ? Date.parse(p.session.expires_at) : Number.POSITIVE_INFINITY;
  return fin > maintenant;
}

/** The selection marker of the user picked by PIN now, or null. */
export const lireSelection = () => lireJson(local(), K.SELECTION);

/** True when the session of this browser is a user picked by PIN on the workstation. */
export const estSelectionEquipe = () => Boolean(lireSelection()?.id);

/** Keep the PIN switch's answer as the selection marker (the JWT itself is the authStore's). */
export function enregistrerSelection(r, maintenant = Date.now()) {
  const s = {
    id: r.selection?.id ?? null,
    user_id: r.user?.id ?? null,
    session_id: r.session?.id ?? null,
    jusqu_a: r.expires_at ?? r.selection?.jusqu_a ?? null,
    delai_inactivite_minutes: r.delai_inactivite_minutes ?? null,
    prolonge_le: maintenant,
  };
  ecrireJson(local(), K.SELECTION, s);
  return s;
}

/** Record a prolongation's new deadline (shared by the windows: one call per step for all of them). */
export function majSelection(patch) {
  const s = lireSelection();
  if (!s) return null;
  const n = { ...s, ...patch };
  ecrireJson(local(), K.SELECTION, n);
  return n;
}

export const oublierSelection = () => ecrireJson(local(), K.SELECTION, null);

/**
 * Forget a user's browser state outside the auth keys: the caisse windows' tab mirror and the caisse
 * snapshots. The caisse login session id (`pharmakon.caisse.session:<uid>`) stays: the same user picked
 * again finds their server drafts (XD-39, XD-327).
 */
export function oublierEtatNavigateur(userId) {
  try {
    if (userId) onglet()?.removeItem(cleMiroir(userId));
    const l = local();
    const drop = [];
    for (let i = 0; i < (l?.length ?? 0); i += 1) {
      const k = l.key(i);
      if (k && k.startsWith("pharmakon_caisse_current")) drop.push(k);
    }
    drop.forEach((k) => l.removeItem(k));
  } catch {
    /* private mode — nothing kept anyway */
  }
}

// ── the team token ──────────────────────────────────────────────────────────

/** A team token with more than the margin left. */
export function jetonFrais(poste, maintenant = Date.now()) {
  if (!poste?.access_token || !poste.expires_at) return false;
  return Date.parse(poste.expires_at) - maintenant > W.MARGE_JETON_POSTE_MS;
}

const posteFerme = (cause) => Object.assign(new Error(cause?.message ?? M.choix.posteTermine), { posteFerme: true, cause });
const attendreParDefaut = (ms) => new Promise((r) => setTimeout(r, ms));
const FIN_POSTE = new Set([CODES.SESSION_POSTE_INVALIDE, CODES.VALIDATION_ERROR, CODES.TOKEN_INVALID]);

function avecVerrou(fn) {
  const locks = typeof navigator !== "undefined" ? navigator.locks : undefined;
  return locks?.request ? locks.request(K.VERROU_RENOUVELLEMENT, fn) : fn();
}

let renouvellement = null; // this window's renewal in flight

/**
 * A usable team token: the stored one while fresh, else ONE renewal for every caller of every window.
 *
 * @param {{ renouveler?: (rt: string) => Promise<object>, maintenant?: () => number, attendre?: (ms: number) => Promise<void> }} [deps]
 * @returns {Promise<string>} rejects `{ posteFerme: true }` when the workstation is over
 */
export async function jetonEquipe({ renouveler = (rt) => equipeApi.renouveler(rt).then((r) => r.data ?? r), maintenant = Date.now, attendre = attendreParDefaut } = {}) {
  const p = lirePoste();
  if (!p?.refresh_token) throw posteFerme();
  if (jetonFrais(p, maintenant())) return p.access_token;
  if (!renouvellement) {
    renouvellement = avecVerrou(async () => {
      const q = lirePoste();
      if (!q?.refresh_token) throw posteFerme();
      // another window renewed while this one waited for the lock
      if (q.refresh_token !== p.refresh_token && jetonFrais(q, maintenant())) return q.access_token;
      try {
        return enregistrerPoste(await renouveler(q.refresh_token)).access_token;
      } catch (e) {
        if (e?.code === CODES.SESSION_POSTE_DEJA_RENOUVELEE) {
          // Rotated by another window in the grace window: its pair reaches the storage. The rotated
          // token is never sent again — that would be a replay, which revokes the workstation.
          for (let i = 0; i < W.RELECTURES_MAX; i += 1) {
            await attendre(W.RELECTURE_MS);
            const n = lirePoste();
            if (n?.refresh_token && n.refresh_token !== q.refresh_token) return n.access_token;
          }
          throw e;
        }
        if (FIN_POSTE.has(e?.code)) {
          effacerPoste();
          throw posteFerme(e);
        }
        throw e;
      }
    }).finally(() => {
      renouvellement = null;
    });
  }
  return renouvellement;
}

/**
 * Run a team-token call; a 401 (the token died early: revoked, rotated password) renews once and retries;
 * a workstation that is over rejects `{ posteFerme: true }`.
 */
export async function appelEquipe(fn) {
  const t = await jetonEquipe();
  try {
    return await fn(t);
  } catch (e) {
    if (e?.code !== CODES.TOKEN_INVALID && e?.code !== CODES.TOKEN_EXPIRED) throw e;
    const p = lirePoste();
    if (p) ecrireJson(local(), K.POSTE, { ...p, expires_at: null });
    return fn(await jetonEquipe());
  }
}

// ── the picked user's session ───────────────────────────────────────────────

/** Prolong only after activity since the last prolongation, at most once per step, while alive. */
export function doitProlonger({ maintenant, derniere_activite, prolonge_le, jusqu_a }) {
  if (!jusqu_a || Date.parse(jusqu_a) <= maintenant) return false;
  if (!(derniere_activite > (prolonge_le ?? 0))) return false;
  return maintenant - (prolonge_le ?? 0) >= W.PAS_PROLONGATION_MS;
}

/** The screen goes back to the picker just before the server's deadline (or without one). */
export function doitRetournerAuChoix({ maintenant, jusqu_a }) {
  if (!jusqu_a) return true;
  return Date.parse(jusqu_a) - maintenant <= W.MARGE_RETOUR_CHOIX_MS;
}

let purgeUtilisateur = null;
/** authStore registers how the user's own keys (token, user) are forgotten. */
export function brancherPurgeUtilisateur(fn) {
  purgeUtilisateur = fn;
}

/**
 * The sentence the picker shows after a return. A pure read (React may render twice in development):
 * the picker forgets it with `oublierMessageChoix` once mounted.
 */
export function lireMessageChoix() {
  try {
    const motif = onglet()?.getItem(K.MESSAGE_CHOIX);
    return motif ? (M.choix.retours[motif] ?? null) : null;
  } catch {
    return null;
  }
}

/** The return sentence is shown once: forgotten after the picker mounted. */
export function oublierMessageChoix() {
  try {
    onglet()?.removeItem(K.MESSAGE_CHOIX);
  } catch {
    /* private mode */
  }
}

function aller(chemin) {
  if (typeof window !== "undefined" && window.location) window.location.replace(chemin);
}

/**
 * Forget the picked user in this browser and reload the picker (every in-memory store goes with the
 * page). The workstation stays; the server drafts persist.
 */
export function retournerAuChoix(motif = MOTIFS_RETOUR_CHOIX.TERMINEE) {
  const s = lireSelection();
  oublierSelection();
  oublierEtatNavigateur(s?.user_id ?? null);
  try {
    useCaisseFenetresStore.setState({ statut: "inactif", userId: null });
  } catch {
    /* not mounted */
  }
  purgeUtilisateur?.();
  try {
    onglet()?.setItem(K.MESSAGE_CHOIX, motif);
  } catch {
    /* private mode */
  }
  aller(posteOuvert() ? R.CHOIX : R.CONNEXION);
}

/** Commit the pending caisse changes of the user still picked (before « Changer d'utilisateur »). */
export async function viderFenetresCaisse() {
  try {
    const s = useCaisseFenetresStore.getState();
    await Promise.all(Object.keys(s.fenetres ?? {}).map((id) => s.vider(id).catch(() => null)));
  } catch {
    /* nothing to flush */
  }
}

/** Prolong the picked user's JWT now (the authStore stores the new token). */
async function prolonger(enregistrerJeton) {
  const r = await equipeApi.prolonger();
  const d = r?.data ?? r;
  enregistrerJeton?.(d.access_token);
  majSelection({ jusqu_a: d.expires_at, prolonge_le: Date.now() });
  return d;
}

/**
 * Follow the picked user's activity while their screen is open: prolong while active, go back to the
 * picker when idle, follow another window's switch. Returns the stop function.
 *
 * @param {{ enregistrerJeton: (token: string) => void }} deps
 */
export function demarrerSuiviSelection({ enregistrerJeton }) {
  if (typeof window === "undefined") return () => {};
  const depart = lireSelection();
  if (!depart?.id) return () => {};
  let derniere_activite = Date.now();
  let enCours = false;
  const activite = () => {
    derniere_activite = Date.now();
  };
  const evenements = ["keydown", "pointerdown", "wheel", "touchstart"];
  evenements.forEach((e) => window.addEventListener(e, activite, { passive: true, capture: true }));

  const tic = async () => {
    const s = lireSelection();
    if (!s?.id || s.id !== depart.id) return;
    const maintenant = Date.now();
    if (doitRetournerAuChoix({ maintenant, jusqu_a: s.jusqu_a })) {
      retournerAuChoix(MOTIFS_RETOUR_CHOIX.INACTIVITE);
      return;
    }
    if (enCours || !doitProlonger({ maintenant, derniere_activite, prolonge_le: s.prolonge_le, jusqu_a: s.jusqu_a })) return;
    enCours = true;
    // claim the step for every window before the call
    majSelection({ prolonge_le: maintenant });
    try {
      await prolonger(enregistrerJeton);
    } catch (e) {
      if (e?.code === CODES.SESSION_EQUIPE_TERMINEE || e?.code === CODES.TOKEN_EXPIRED || e?.code === CODES.TOKEN_INVALID) {
        retournerAuChoix(MOTIFS_RETOUR_CHOIX.TERMINEE);
      }
    } finally {
      enCours = false;
    }
  };
  const minuterie = window.setInterval(tic, W.TIC_SUIVI_MS);

  const stockage = (e) => {
    if (e.key !== K.SELECTION && e.key !== K.POSTE) return;
    const s = lireSelection();
    // another window switched user or closed the workstation: this one follows
    if (!s?.id || s.id !== depart.id) aller(posteOuvert() ? R.CHOIX : R.LOGIN);
  };
  window.addEventListener("storage", stockage);

  return () => {
    evenements.forEach((e) => window.removeEventListener(e, activite, { capture: true }));
    window.clearInterval(minuterie);
    window.removeEventListener("storage", stockage);
  };
}

// ── the owner's step-up (XD-296) ────────────────────────────────────────────

/** The password prompt's state: open while a request waits for the owner's proof. */
export const usePreuveEquipe = create(() => ({ ouverte: false, attentes: [] }));

/** Ask the owner's password once for every request waiting on it (resolves when proved). */
export function demanderPreuve() {
  return new Promise((resolve, reject) => {
    usePreuveEquipe.setState((s) => ({ ouverte: true, attentes: [...s.attentes, { resolve, reject }] }));
  });
}

/** The prompt's outcome: proved (every waiting request replays) or cancelled (they reject). */
export function conclurePreuve(ok) {
  const { attentes } = usePreuveEquipe.getState();
  usePreuveEquipe.setState({ ouverte: false, attentes: [] });
  for (const a of attentes) {
    if (ok) a.resolve();
    else a.reject(new Error("preuve annulée"));
  }
}

brancherSessionEquipe({
  active: estSelectionEquipe,
  terminee: () => retournerAuChoix(MOTIFS_RETOUR_CHOIX.TERMINEE),
  preuve: demanderPreuve,
});

// ── the picker and the PIN pad ──────────────────────────────────────────────

/** Lower case, accents dropped, spaces folded (the type-to-filter of the picker). */
export const normaliser = (v) =>
  String(v ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();

/** The users whose first name / name contain every typed word (accents and case ignored). */
export function filtrerChoix(utilisateurs, saisie) {
  const mots = normaliser(saisie).split(" ").filter(Boolean);
  if (!mots.length) return [...(utilisateurs ?? [])];
  return (utilisateurs ?? []).filter((u) => {
    const nom = normaliser(`${u.prenom ?? ""} ${u.nom ?? ""}`);
    return mots.every((m) => nom.includes(m));
  });
}

/** The option the arrows move to (stops at the ends; a disabled user stays reachable to read why). */
export function optionVoisine(liste, index, delta) {
  const n = liste?.length ?? 0;
  if (!n) return -1;
  if (index < 0) return 0;
  return Math.max(0, Math.min(n - 1, index + delta));
}

/** Only a selectable user opens the PIN pad. */
export const pinSaisissable = (u) => Boolean(u?.selectionnable);

/** The PIN pad's reducer: digits only, bounded; Retour arrière removes one, Suppr clears. */
export function saisiePin(etat, touche) {
  let chiffres = etat?.chiffres ?? "";
  if (/^\d$/.test(touche)) {
    if (chiffres.length < W.PIN_SAISIE_MAX) chiffres += touche;
  } else if (touche === "Backspace") chiffres = chiffres.slice(0, -1);
  else if (touche === "Delete" || touche === "Clear") chiffres = "";
  return { chiffres, pret: chiffres.length >= W.PIN_LONGUEUR_MIN };
}

/** Keep the digits of a typed / pasted value, bounded (the masked field's onChange). */
export const chiffresPin = (v) => String(v ?? "").replace(/\D/g, "").slice(0, W.PIN_SAISIE_MAX);

const heure = (d) => fmtTime(d);

/** A refused PIN switch as one sentence; `verrouille` disables the pad. */
export function messageRefusPin(err) {
  if (!err?.code) return { texte: err?.message && !err?.isAxiosError ? err.message : M.erreurReseau, verrouille: false };
  if (err.code === CODES.PIN_VERROUILLE) {
    const jusqu = err.details?.verrou_jusqu_a;
    return { texte: jusqu ? M.pin.verrouilleJusqua(heure(jusqu)) : err.message, verrouille: true };
  }
  if (err.code === CODES.PIN_NON_DEFINI) return { texte: M.raisons.PIN_NON_DEFINI, verrouille: true };
  return { texte: err.message ?? M.erreurReseau, verrouille: false };
}

const champErreur = (err) => {
  for (const e of Array.isArray(err?.errors) ? err.errors : []) {
    const m = /^body\.(\w+)$/.exec(e?.field ?? "");
    if (m) return { champ: m[1], message: e.message };
  }
  return err?.details?.champ ? { champ: err.details.champ, message: err.message } : null;
};
const CHAMPS_POSTE = new Set(["libelle_poste", "duree_jours", "delai_inactivite_minutes"]);

/** A refused verification: the step to show again and its sentence. */
export function messageRefusCode(err) {
  if (err?.code === CODES.CODE_EQUIPE_INVALIDE) {
    const n = err.details?.essais_restants;
    if (Number.isInteger(n) && n > 0) return { etape: ETAPES_CONNEXION.CODE, texte: M.connexion.codeIncorrect(n) };
    return { etape: ETAPES_CONNEXION.IDENTIFIANTS, texte: err.message ?? M.erreurReseau };
  }
  const champ = champErreur(err);
  if (champ && CHAMPS_POSTE.has(champ.champ)) return { etape: ETAPES_CONNEXION.POSTE, texte: champ.message };
  if (champ?.champ === "code_connexion") return { etape: ETAPES_CONNEXION.CODE, texte: champ.message };
  if (err?.code === CODES.VALIDATION_ERROR) return { etape: ETAPES_CONNEXION.POSTE, texte: err.message };
  return { etape: ETAPES_CONNEXION.IDENTIFIANTS, texte: err?.code ? err.message : M.erreurReseau };
}

/** A refused team login step 1 as one sentence (the lock with its end time). */
export function messageRefusConnexion(err) {
  if (err?.code === CODES.ACCOUNT_LOCKED && err.details?.verrou_jusqu_a) {
    return M.connexion.verrouilleJusqua(heure(err.details.verrou_jusqu_a));
  }
  return err?.code ? err.message : M.erreurReseau;
}

// ── choices, home, rows ─────────────────────────────────────────────────────

const pluriel = (n, un, plusieurs) => `${n} ${n > 1 ? plusieurs : un}`;

/** The saved-duration choices (the mirror's list). */
export const optionsDuree = () => W.DUREES_JOURS.map((j) => ({ value: String(j), label: pluriel(j, "jour", "jours") }));

/** The idle-delay choices (the mirror's list). */
export const optionsDelai = () => W.DELAIS_INACTIVITE_MINUTES.map((m) => ({ value: String(m), label: pluriel(m, "minute", "minutes") }));

/** Where a user lands after a login or a PIN switch (the password login's own mapping). */
export function accueilPourRole(role) {
  if (role === "ADMIN_PHARMAKON") return "/admin/dashboard";
  if (role === "CAISSIER") return "/caissier";
  return "/dashboard";
}

/** The role label the team screens show (the CAISSIER code unchanged, CP §15.3). */
export const libelleRole = (role) => (role === "CAISSIER" ? "Caissier(ère)" : (LOCALE_LABELS[role] ?? role ?? ""));

export const nomUtilisateur = (u) => [u?.prenom, u?.nom].filter(Boolean).join(" ");

/** « Chrome · Linux » from a user agent (the owner recognises the workstation, never a raw string). */
export function resumeNavigateur(ua) {
  const s = String(ua ?? "");
  if (!s) return "—";
  const nav = /Edg\//.test(s)
    ? "Edge"
    : /OPR\/|Opera/.test(s)
      ? "Opera"
      : /Firefox\//.test(s)
        ? "Firefox"
        : /Chrome\/|Chromium\//.test(s)
          ? "Chrome"
          : /Safari\//.test(s)
            ? "Safari"
            : "Navigateur";
  const sys = /Windows/.test(s) ? "Windows" : /Android/.test(s) ? "Android" : /iPhone|iPad|iOS/.test(s) ? "iOS" : /Mac OS X|Macintosh/.test(s) ? "macOS" : /Linux|X11/.test(s) ? "Linux" : null;
  return sys ? `${nav} · ${sys}` : nav;
}

const dateHeure = (d) => (d ? `${fmtDate(d)} ${fmtTime(d)}` : M.securite.jamais);

/** A row of « Sessions actives ». */
export function presentationSession(s) {
  return {
    id: s.id,
    libelle: s.libelle_poste ?? "—",
    etat: s.etat,
    etatLibelle: M.securite.etatLigne[s.etat] ?? s.etat,
    ouverte: dateHeure(s.cree_le),
    jusqua: dateHeure(s.expires_at),
    activite: dateHeure(s.derniere_activite),
    navigateur: resumeNavigateur(s.user_agent_creation),
    ip: s.ip_creation ?? "—",
    utilisateur: s.utilisateur_actuel ? nomUtilisateur(s.utilisateur_actuel) : M.securite.personne,
    role: s.utilisateur_actuel?.role ? libelleRole(s.utilisateur_actuel.role) : null,
    revocable: s.etat === "ACTIVE",
  };
}

/** The reason line of a picker option (its message, with the lock's end time when known). */
export function raisonOption(u) {
  if (u?.selectionnable) return null;
  if (u?.raison === RAISONS_NON_SELECTIONNABLE.PIN_VERROUILLE && u.verrou_jusqu_a) {
    return `${M.raisons.PIN_VERROUILLE} Réessayez après ${heure(u.verrou_jusqu_a)}.`;
  }
  return u?.message ?? M.raisons[u?.raison] ?? null;
}

// ── the picker's photos (the team token, never a public URL) ────────────────

const photos = new Map(); // `${id}@${maj_le}` → Promise<string|null>

/** A Blob URL of a listed user's photo, read with the team token (null without a photo). */
export function urlPhotoPoste(u) {
  const maj = u?.photo_maj_le ?? u?.photo?.maj_le ?? null;
  if (!u?.id || !maj) return Promise.resolve(null);
  const cle = `${u.id}@${maj}`;
  if (!photos.has(cle)) {
    photos.set(
      cle,
      appelEquipe((t) => equipeApi.photo(t, u.id))
        .then((blob) => (typeof Blob !== "undefined" && blob instanceof Blob ? URL.createObjectURL(blob) : null))
        .catch(() => {
          photos.delete(cle);
          return null;
        }),
    );
  }
  return photos.get(cle);
}
